<template>
  <div>
    <Header />
    <div class="title-container">
      <h1>Withdrawn Logbooks</h1>
    </div>
    <div class="container-fluid-logbook">
      <vue-good-table :columns="columns" :rows="processedRows" :pagination-options="{ enabled: true, perPage: perPage }"
        :search-options="{ enabled: false }"
        :sort-options="{ enabled: true, initialSortBy: { field: 'withdrawn_at', type: 'desc' } }">
        <template #emptystate>
          <div class="empty-state">{{ loading ? 'Loading...' : 'No withdrawn Logbooks.' }}</div>
        </template>
        <template #table-row="props">
          <!-- Project -->
          <span v-if="props.column.field === 'project_id'">
            <span :title="props.row.project_name">{{ props.row.project_id }}</span>
          </span>
          <!-- Reason -->
          <span v-else-if="props.column.field === 'withdrawn_reason'">
            <span :class="['status-badge', `reason-${props.row.withdrawn_reason}`]">
              {{ formatReason(props.row.withdrawn_reason) }}
            </span>
          </span>
          <!-- Actions -->
          <span v-else-if="props.column.field === 'actions'" class="center-icons">
            <font-awesome-icon icon="undo" class="restore-icon" title="Restore logbook"
              @click="restoreLogbook(props.row)" />
          </span>
          <!-- Default Cell -->
          <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>

<script>
import Header from '../Header.vue';
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';

const REASONS = { medical: 'Medical', personal: 'Personal', other: 'Other' };

export default {
  name: 'WithdrawnLogbooks',

  components: {
    VueGoodTable,
    Header,
  },

  data() {
    return {
      logbooks: [],
      perPage: 10,
      loading: true,
    };
  },

  computed: {
    columns() {
      return [
        { label: 'ID', field: 'logbook_id', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Logbook Name', field: 'name', sortable: true, thClass: 'vgt-logbook-name-column', tdClass: 'vgt-logbook-name-column' },
        { label: `${this.$labels.project} ID`, field: 'project_id', sortable: true, thClass: 'vgt-foreign-id-column', tdClass: 'vgt-id-column' },
        {
          label: 'Trainee',
          field: 'trainee_full_name',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterFn: (rowValue, filterValue) => {
              if (!filterValue) return true;
              return String(rowValue || '').toLowerCase().includes(String(filterValue).toLowerCase());
            }
          }
        },
        { label: 'Entries', field: 'entries_count', sortable: true, thClass: 'vgt-status-column vgt-left-align-column' },
        {
          label: 'Reason',
          field: 'withdrawn_reason',
          sortable: true,
          thClass: 'vgt-status-column vgt-left-align-column',
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: Object.entries(REASONS).map(([value, text]) => ({ value, text })),
          }
        },
        { label: 'Details', field: 'withdrawn_note', sortable: false },
        { label: 'Withdrawn By', field: 'withdrawn_by_name', sortable: true, thClass: 'vgt-withdrawn-column' },
        { label: 'Withdrawn At', field: 'withdrawn_at', sortable: true, thClass: 'vgt-withdrawn-column', formatFn: this.formatDate },
        { label: 'Restore', field: 'actions', sortable: false, thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ];
    },
    processedRows() {
      return this.logbooks.map(lb => {
        const last = lb.trainee?.last_name || '';
        const first = lb.trainee?.first_name || '';
        return {
          ...lb,
          project_name: lb.project ? lb.project.name : `Unknown ${this.$labels.project}`,
          trainee_full_name: (last || first) ? `${last}, ${first}` : '',
          withdrawn_by_name: lb.withdrawn_by_name || 'Unknown',
        };
      });
    },
  },

  methods: {
    formatReason(reason) {
      return REASONS[reason] || reason;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : '';
    },
    async fetchWithdrawn() {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      this.loading = true;
      try {
        const response = await axios.get('/api/logbooks/withdrawn', { headers });
        this.logbooks = response.data;
      } catch (error) {
        console.error('An error occurred while fetching withdrawn logbooks:', error);
        if (error.response?.status === 403) {
          alert('Only admins can view withdrawn Logbooks.');
          this.$router.back();
        }
      } finally {
        this.loading = false;
      }
    },
    async restoreLogbook(logbook) {
      if (!confirm(`Restore Logbook ${logbook.logbook_id} (${logbook.trainee_full_name || logbook.name})?\n\n`
        + `It will be returned to ${this.$labels.project} ${logbook.project_id} and the ${this.$labels.project}'s current instructors will be re-assigned.`)) return;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        await axios.post(`/api/logbooks/${logbook.logbook_id}/restore-withdrawn`, {}, { headers });
        this.logbooks = this.logbooks.filter(a => String(a.logbook_id) !== String(logbook.logbook_id));
        alert('Logbook restored successfully');
      } catch (error) {
        if (error.isOffline === true) {
          alert('Logbooks cannot be restored while offline. Please try again when the connection is restored.');
        } else if (error.response?.status === 404) {
          alert('This Logbook is no longer withdrawn.');
          this.fetchWithdrawn();
        } else {
          alert('An error occurred while restoring the Logbook.');
        }
      }
    },
  },

  mounted() {
    this.fetchWithdrawn();
  },
}
</script>

<style scoped>
.container-fluid-logbook {
  padding-top: 10px;
  width: 98%;
  margin-left: 1%;
  margin-right: 1%;
  display: flex;
  justify-content: center;
}

h1 {
  padding-top: 0px;
  padding-left: 3%;
}

.page-info {
  padding-left: 3%;
  padding-right: 3%;
  font-size: 14px;
  color: #555;
  text-align: left;
}

.empty-state {
  text-align: center;
  padding: 20px;
  color: #777;
}

.restore-icon {
  font-size: 16px;
  color: #2952c4;
  cursor: pointer;
  transition: color 0.2s, transform 0.2s;
}

.restore-icon:hover {
  color: #0c1a61;
  transform: scale(1.1);
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px !important;
  color: #fff;
  background: #6c757d;
}

.reason-medical {
  background: #dc3545;
}

.reason-personal {
  background: #398dec;
}
</style>
